import { useRouter } from 'next/router';

import { getConfigByLocale } from '@otovo/shared/cloudConfig/cloudConfig';
import CenterSpinner from '@otovo/shared/components/LoadingScreens/CenterSpinner';
import { StaticPropsContext } from '@otovo/shared/lib/next/types';
import {
  Sanity$CampaignPage,
  Sanity$DocumentType,
} from '@otovo/shared/types/sanityTypes';
import { useQuery } from '@sanity/react-loader';
import { InferGetStaticPropsType } from 'next';
import { loadQuery, loadQueryOptions } from '../../components/Sanity/ssr';
import SanityLandingPage from '../../components/SanityLandingPage/SanityLandingPage';
import { landingPageQuery } from '../../components/SanityLandingPage/sanityQueries';
import StorefrontPage from '../../components/StorefrontPage';
import {
  getStaticProps as getStaticMarketProps,
  getStaticPathsForSanityPages,
} from '../../config/getProps';

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Partner = ({ config, initial, query }: PageProps) => {
  const router = useRouter();
  const { data } = useQuery(query, {}, { initial });
  const { hideMenu, hideFooter } = data || {};

  if (router.isFallback) {
    return <CenterSpinner />;
  }

  return (
    <StorefrontPage config={config} hideMenu={hideMenu} hideFooter={hideFooter}>
      <SanityLandingPage page={data} />
    </StorefrontPage>
  );
};

const PARTNER_PAGE_TYPE: Sanity$DocumentType = 'partnerPage';

export async function getStaticPaths() {
  return getStaticPathsForSanityPages({
    pageType: PARTNER_PAGE_TYPE,
  });
}

export async function getStaticProps(context: StaticPropsContext) {
  const { draftMode = false } = context;
  const buConfig = getConfigByLocale(context.locale);

  const sanityOptions = loadQueryOptions(context);
  const { queryString } = landingPageQuery(
    PARTNER_PAGE_TYPE,
    context.params.slug,
    buConfig,
    context.locale,
  );

  const initial = await loadQuery<Sanity$CampaignPage>(
    queryString,
    {},
    sanityOptions,
  );

  // Then resolve props, potentially matching with a partner
  const { props, revalidate } = await getStaticMarketProps(context, {
    partnerSlug: initial.data?.utmSource,
  });

  return {
    props: {
      ...props,
      sanityDocument: initial.data || null,
      initial: initial,
      query: queryString,
      draftMode,
    },
    revalidate,
    notFound: !initial.data,
  };
}

export default Partner;
